import { Injectable } from '@angular/core';
import { BehaviorSubject, delayWhen, interval, of, switchMap, tap, throwError } from 'rxjs';
import { TenantService } from '@services/tenant-services/tenant.service';
import { AuthService } from '@services/auth-services/auth.service';
import { Tenant } from '@services/tenant-services/models';
import { catchError, map } from 'rxjs/operators';
import { User } from '@services/core-services/models';
import { Store } from '@services/setting-services/models';
import { TokenService } from '@services/auth-services/token.service';
import { environment } from '../../../environments/environment';
import { ApplicationMode, AppService } from '@services/core-services/app.service';

const ARTIFICIAL_DELAY = environment.loadingArtificialDelay;

export enum ProtectionError {
  MISSING_SUBDOMAIN = 'MISSING_SUBDOMAIN',
  UNKNOWN_TENANT = 'UNKNOWN_TENANT',
  UNKNOWN_USER = 'UNKNOWN_USER',
  MISSING_STORE_SLUG = 'MISSING_STORE_SLUG',
  UNKNOWN_STORE = 'UNKNOWN_STORE',
}

@Injectable({ providedIn: 'root' })
export class ProtectionService {
  private readonly error = new BehaviorSubject<any>(undefined);
  private readonly store = new BehaviorSubject<Store | undefined>(undefined);

  constructor(
    private readonly appService: AppService,
    private readonly tenantService: TenantService,
    private readonly authService: AuthService,
    private readonly tokenService: TokenService
  ) {}

  getError() {
    return this.error.getValue();
  }

  getTenant() {
    return this.tenantService.getCurrentTenant();
  }

  getUser() {
    return this.authService.getCurrentUser();
  }

  getCurrentStoreSnapshot() {
    return this.store.getValue();
  }

  getStoreAndApp() {
    const store = this.store.getValue();
    const mode = this.appService.getApplicationMode();
    return of({ mode, store });
  }

  getStoreAndAppModeSnapshot() {
    const store = this.store.getValue() || this.getDefaultStore();
    const mode = this.appService.getApplicationMode();
    return { mode, store };
  }

  getStore() {
    return this.store.asObservable();
  }

  setStore(store: Store | undefined) {
    this.tokenService.setCurrentStoreId(store!.id);
    this.store.next(store);
    return store;
  }

  getDefaultStore() {
    const user = this.getUser();
    return this.tenantService.getCompletedStore(user);
  }

  isSetupAlready() {
    const tenant = this.getTenant();
    const user = this.getUser();
    const store = this.getCurrentStoreSnapshot();
    return tenant && user && store;
  }

  /**
   * This checks tenant, user and store for the request
   */
  protectAll(subdomain: string | undefined, slug: string | undefined, mode: string | undefined) {
    return of(null).pipe(
      delayWhen(() => interval(ARTIFICIAL_DELAY)),
      switchMap(() => this.protectTenant(subdomain)),
      switchMap(() => this.protectUser()),
      switchMap(() => this.protectAppMode(mode)),
      switchMap(() => this.protectStore(slug)),
      tap(() => true),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  /**
   * This checks tenant and user for the request
   */
  protectTenantAndUser(subdomain: string | undefined) {
    return of(null).pipe(
      delayWhen(() => interval(ARTIFICIAL_DELAY)),
      switchMap(() => this.protectTenant(subdomain)),
      switchMap(() => this.protectUser()),
      tap(() => true),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  protectAppMode(slug: string | undefined) {
    if (slug) {
      const mode: ApplicationMode = slug === 'p' ? ApplicationMode.Live : ApplicationMode.Sandbox;
      return this.appService.setApplicationMode(mode);
    }

    return of(this.appService.getApplicationMode());
  }

  protectStore(slug: string | undefined) {
    if (!slug) {
      return this.setAndThrowError(ProtectionError.MISSING_STORE_SLUG);
    }

    // Is current store in state just return true
    const currentStore = this.getCurrentStoreSnapshot();
    if (currentStore && currentStore.slug === slug) {
      return of(true);
    }

    const tenant = this.getTenant();
    const store = tenant?.stores.find(s => s.slug === slug);
    return of(store).pipe(
      tap(store => this.setStore(store)),
      map(store => !!store),
      catchError(() => {
        return this.setAndThrowError(ProtectionError.UNKNOWN_STORE);
      })
    );
  }

  protectUser() {
    // Is user in state just return true
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      return of(true);
    }

    return this.authService.getAuth().pipe(
      tap(user => this.ensureUser(user)),
      tap(user => this.authService.setCurrentUser(user)),
      map(user => !!user),
      catchError(err => {
        return this.setAndThrowError(ProtectionError.UNKNOWN_USER);
      })
    );
  }

  protectTenant(subdomain: string | undefined, standalone: boolean = false) {
    if (!subdomain) {
      return this.setAndThrowError(ProtectionError.MISSING_SUBDOMAIN);
    }

    // Is current tenant in state just return true
    const currentTenant = this.tenantService.getCurrentTenant();
    if (currentTenant) {
      return of(true);
    }

    return this.tenantService.getTenantBySubdomain(subdomain).pipe(
      tap(tenant => this.ensureTenant(tenant)),
      tap(tenant => this.tenantService.setTenant(tenant)),
      map(tenant => !!tenant),
      map(passed => {
        return passed;
      }),
      catchError(err => {
        return this.setAndThrowError(ProtectionError.UNKNOWN_TENANT);
      })
    );
  }

  private ensureUser(user: User) {
    if (user) return of(user);
    this.authService.setCurrentUser(undefined);
    return this.setAndThrowError(ProtectionError.UNKNOWN_USER);
  }

  private ensureTenant(tenant: Tenant) {
    if (tenant) return of(tenant);
    this.tenantService.setTenant(undefined);
    return this.setAndThrowError(ProtectionError.UNKNOWN_TENANT);
  }

  private setAndThrowError(error: any) {
    this.error.next(error);
    return throwError(() => error);
  }
}
